import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Trophy, Award, Target, Brain, Calendar, TrendingUp, MessageSquare, Activity, Eye, Apple, Droplets, Moon, Users, Video } from 'lucide-react';
import HydrationTracker from '../HydrationTracker';
import HydrationReminder from '../HydrationReminder';
import NutritionTracker from '../NutritionTracker';
import SleepTracker from '../SleepTracker';
import ActivityTracker from '../ActivityTracker';
import ConfirmDialog from '../ConfirmDialog';
import StatCard, { StreakCard } from '../shared/StatCard';
import Modal, { ModalActions } from '../shared/Modal';
import DailyPromptCard from '../shared/DailyPromptCard';
import DashboardHeader from '../dashboard/DashboardHeader';
import { GrindIntentScreen, VideoReviewModule } from '../grind';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, refreshUser, justLoggedIn, clearJustLoggedIn } = useAuth();
  const [dailyPrompts, setDailyPrompts] = useState([]);
  const [userProgress, setUserProgress] = useState(null);
  const [badges, setBadges] = useState([]);
  const [coachChallenges, setCoachChallenges] = useState([]);
  const [challengeNotes, setChallengeNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [showTeamComparisonModal, setShowTeamComparisonModal] = useState(false);
  const [teamComparison, setTeamComparison] = useState(null);
  const [teamComparisonLoading, setTeamComparisonLoading] = useState(false);
  const [comparisonView, setComparisonView] = useState('team'); // 'team', 'club', or 'global'
  const [globalRanking, setGlobalRanking] = useState(null);
  const [globalRankingLoading, setGlobalRankingLoading] = useState(false);
  const [showPointsAnalyticsModal, setShowPointsAnalyticsModal] = useState(false);
  const [pointsAnalytics, setPointsAnalytics] = useState(null);
  const [pointsAnalyticsLoading, setPointsAnalyticsLoading] = useState(false);
  const [showBadgesModal, setShowBadgesModal] = useState(false);
  const [showSportFocusModal, setShowSportFocusModal] = useState(false);
  const [sportFocusValue, setSportFocusValue] = useState('');
  const [sportStrengths, setSportStrengths] = useState([]);
  const [sportWeaknesses, setSportWeaknesses] = useState([]);
  const [userTeams, setUserTeams] = useState([]);
  
  // Grind Intent & Video Review states
  const [showGrindIntent, setShowGrindIntent] = useState(false);
  const [grindDate, setGrindDate] = useState(null);
  const [showVideoReview, setShowVideoReview] = useState(false);
  
  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null,
    variant: 'default',
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // Show Grind Intent EVERY time user logs in
  useEffect(() => {
    if (justLoggedIn) {
      setShowGrindIntent(true);
      // Clear the flag after showing - this is per-login, not per-page-visit
      clearJustLoggedIn();
    }
  }, [justLoggedIn, clearJustLoggedIn]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    const fetchUserTeams = async () => {
      if (user) {
        try {
          const response = await axios.get(`${API}/teams`);
          const teams = Array.isArray(response.data) ? response.data : [];
          setUserTeams(teams);
        } catch (error) {
          // Failed to load user teams - non-critical
        }
      }
    };
    fetchUserTeams();
  }, [user]);

  const loadDashboardData = async () => {
    try {
      const [promptsRes, progressRes, badgesRes, challengesRes] = await Promise.all([
        axios.get(`${API}/prompts/daily`),
        axios.get(`${API}/user/progress`),
        axios.get(`${API}/badges`),
        axios.get(`${API}/athlete/challenges`)
      ]);

      setDailyPrompts(promptsRes.data);
      setUserProgress(progressRes.data);
      setBadges(badgesRes.data);
      setCoachChallenges(challengesRes.data.challenges || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const completeCoachChallenge = async (challengeId) => {
    const notes = challengeNotes[challengeId];
    if (!notes?.trim()) {
      toast.error('Please describe how you completed the challenge');
      return;
    }

    try {
      const response = await axios.post(`${API}/athlete/challenge/complete`, {
        challenge_id: challengeId,
        completion_notes: notes
      });

      toast.success(`Challenge completed! +${response.data.points_earned} points`);
      
      // Show badge notifications
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        response.data.new_badges.forEach(badge => {
          setTimeout(() => {
            toast.success(`🏆 Badge Earned: ${badge}!`, { duration: 5000 });
          }, 1000);
        });
      }

      // Reload dashboard data
      await loadDashboardData();
      await refreshUser();
      
      // Clear the note
      setChallengeNotes({...challengeNotes, [challengeId]: ''});
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Challenge already completed');
      } else {
        toast.error('Failed to complete challenge');
      }
    }
  };

  const loadUserActivities = async () => {
    setActivitiesLoading(true);
    try {
      const response = await axios.get(`${API}/user/activities?days=14`);
      setActivities(response.data);
      setShowActivitiesModal(true);
    } catch (error) {
      console.error('Error loading activities:', error);
      toast.error('Failed to load activity history');
    } finally {
      setActivitiesLoading(false);
    }
  };

  const loadTeamComparison = async () => {
    setTeamComparisonLoading(true);
    setComparisonView('team'); // Reset to team view when opening
    try {
      const response = await axios.get(`${API}/user/team-level-comparison`);
      
      if (!response.data.is_team_member) {
        // Still show modal but with global data only
        setTeamComparison(null);
      } else {
        setTeamComparison(response.data);
      }
      
      // Also load global ranking
      loadGlobalRanking();
      
      setShowTeamComparisonModal(true);
    } catch (error) {
      console.error('Error loading team comparison:', error);
      toast.error('Failed to load team comparison');
    } finally {
      setTeamComparisonLoading(false);
    }
  };

  const loadGlobalRanking = async () => {
    setGlobalRankingLoading(true);
    try {
      const response = await axios.get(`${API}/user/global-ranking`);
      setGlobalRanking(response.data);
    } catch (error) {
      console.error('Error loading global ranking:', error);
    } finally {
      setGlobalRankingLoading(false);
    }
  };

  const loadPointsAnalytics = async () => {
    setPointsAnalyticsLoading(true);
    try {
      const response = await axios.get(`${API}/user/points-analytics`);
      setPointsAnalytics(response.data);
      setShowPointsAnalyticsModal(true);
    } catch (error) {
      console.error('Error loading points analytics:', error);
      toast.error('Failed to load points analytics');
    } finally {
      setPointsAnalyticsLoading(false);
    }
  };

  const openBadgesModal = () => {
    setShowBadgesModal(true);
  };

  const openSportFocusModal = () => {
    setSportFocusValue(user?.focused_sport || '');
    setSportStrengths(user?.key_strengths || []);
    setSportWeaknesses(user?.areas_to_improve || []);
    setShowSportFocusModal(true);
  };

  const handleSportFocusUpdate = async () => {
    if (!sportFocusValue.trim()) {
      toast.error('Please enter a sport');
      return;
    }

    if (sportStrengths.length < 3) {
      toast.error('Please select at least 3 key strengths');
      return;
    }

    if (sportWeaknesses.length < 3) {
      toast.error('Please select at least 3 areas to improve');
      return;
    }

    try {
      await axios.put(`${API}/user/sport-focus`, {
        focused_sport: sportFocusValue,
        key_strengths: sportStrengths,
        areas_to_improve: sportWeaknesses
      });
      
      toast.success('Sport focus updated! Affirmations and visualizations will reflect your new focus.');
      await refreshUser();
      setShowSportFocusModal(false);
      
      // Reload daily prompts to get updated affirmations/visualizations
      const promptsRes = await axios.get(`${API}/prompts/daily`);
      setDailyPrompts(promptsRes.data);
    } catch (error) {
      console.error('Error updating sport focus:', error);
      toast.error('Failed to update sport focus');
    }
  };

  const submitResponse = async (promptId, responseText, category) => {
    try {
      const response = await axios.post(`${API}/responses`, {
        prompt_id: promptId,
        response_text: responseText,
        category: category
      });
      
      // Handle success message
      toast.success('Great job! Response submitted successfully');
      
      // Handle badge notifications
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        response.data.new_badges.forEach(badge => {
          setTimeout(() => {
            toast.success(`🏆 Badge Earned: ${badge}!`, { duration: 5000 });
          }, 1000);
        });
      }
      
      // Handle level up notification
      if (response.data.level_increased) {
        setTimeout(() => {
          toast.success(`🎉 Level Up! You've reached a new level!`, { duration: 5000 });
        }, 1500);
      }
      
      loadDashboardData(); // Refresh data
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
      return false;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-slate-9000 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-white animate-pulse" />
          </div>
          <p className="text-slate-50">Loading your athlete dashboard...</p>
        </div>
      </div>
    );
  }

  const categoryIcons = {
    affirmations: Brain,
    visualization: Eye,
    nutrition: Apple,
    hydration: Droplets,
    sleep: () => <span className="text-2xl">💤</span>
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950">
      {/* Grind Intent Screen - shown on first visit or on demand */}
      {showGrindIntent && (
        <GrindIntentScreen
          onSelect={(intent) => {
            setShowGrindIntent(false);
            if (intent === 'yesterday') {
              // Yesterday → Go to Video Review
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              setGrindDate(yesterday.toISOString().split('T')[0]);
              setShowVideoReview(true);
            }
            // Today → Stay on regular dashboard (do nothing extra)
          }}
          onSkip={() => setShowGrindIntent(false)}
        />
      )}
      
      {/* Video Review Module - Full screen overlay */}
      {showVideoReview && (
        <VideoReviewModule
          defaultDate={grindDate}
          onClose={() => {
            setShowVideoReview(false);
            setGrindDate(null);
          }}
          onSwitchToDashboard={() => {
            setShowVideoReview(false);
            setGrindDate(null);
          }}
        />
      )}
      
      {/* Main Dashboard Content - hidden when Video Review is active */}
      {!showVideoReview && (
        <>
      {/* Hydration Reminder - invisible notification component */}
      <HydrationReminder />
      
      <DashboardHeader />
      
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Athlete Stats Overview - Interactive */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            icon={Trophy}
            label="Current Level"
            value={user?.level || 1}
            subtext="📊 Click for details"
            clickable={true}
            onClick={loadTeamComparison}
          />
          <StatCard
            icon={Award}
            label="Total Points"
            value={user?.total_points || 0}
            subtext="📈 Click for analytics"
            clickable={true}
            onClick={loadPointsAnalytics}
          />
          <StatCard
            icon={Target}
            label="Badges Earned"
            value={user?.badges?.length || 0}
            subtext="🏆 Click to view"
            clickable={true}
            onClick={openBadgesModal}
          />
          <StatCard
            icon={Brain}
            label="Sport Focus"
            value={user?.focused_sport || 'Not Set'}
            subtext="✏️ Click to edit"
            clickable={true}
            onClick={openSportFocusModal}
          />
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="today" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 bg-blue-900/90 backdrop-blur-sm border border-blue-700">
            <TabsTrigger value="today" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Today
            </TabsTrigger>
            <TabsTrigger value="progress" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Progress
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white">
              <Activity className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="badges" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-white">
              <Award className="w-4 h-4 mr-2" />
              Badges
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="space-y-4">
            {/* Coach Challenges Section - Moved to Top */}
            {coachChallenges.length > 0 && (
              <div className="space-y-4">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold text-white">Coach Challenges</h2>
                  <p className="text-yellow-400">Complete challenges from your coaches to earn bonus points</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {coachChallenges.map((challenge) => (
                    <Card key={challenge.id} className={`border-2 ${challenge.completed ? 'border-green-500 bg-green-900/20' : 'border-blue-700 bg-blue-900/70'}`}>
                      <CardHeader>
                        <CardTitle className="text-white flex items-center justify-between">
                          <span>{challenge.challenge_title}</span>
                          {challenge.completed && <span className="text-green-400">✓</span>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-gray-300">{challenge.challenge_description}</p>
                        
                        <div className="flex gap-2 flex-wrap">
                          <span className="px-2 py-1 text-xs bg-sky-900 text-sky-300 rounded">{challenge.challenge_type}</span>
                          <span className="px-2 py-1 text-xs bg-yellow-900 text-yellow-300 rounded">{challenge.points_reward} points</span>
                        </div>
                        
                        {!challenge.completed ? (
                          <div className="space-y-2">
                            <Textarea
                              placeholder="Describe how you completed this challenge..."
                              value={challengeNotes[challenge.id] || ''}
                              onChange={(e) => setChallengeNotes({...challengeNotes, [challenge.id]: e.target.value})}
                              rows={2}
                              className="border-blue-700 focus:border-yellow-500 bg-blue-900/50 text-white placeholder:text-gray-400"
                            />
                            <Button
                              onClick={() => completeCoachChallenge(challenge.id)}
                              disabled={!challengeNotes[challenge.id]?.trim()}
                              className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                            >
                              Complete Challenge
                            </Button>
                          </div>
                        ) : (
                          <div className="bg-green-900/30 p-3 rounded border border-green-700">
                            <p className="text-green-300 font-medium mb-1">✓ Completed</p>
                            <p className="text-gray-300 text-sm">{challenge.completion_notes}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(challenge.completed_at).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Daily Prompts Section */}
            <div className={`text-center space-y-2 ${coachChallenges.length > 0 ? 'mt-8' : ''}`}>
              <h2 className="text-2xl font-bold text-white">Today's Challenges</h2>
              <p className="text-yellow-400">Complete your daily prompts to maintain your streaks</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {dailyPrompts.map((prompt) => (
                <DailyPromptCard
                  key={prompt.id}
                  prompt={prompt}
                  onSubmit={submitResponse}
                  userTeams={userTeams}
                  setConfirmDialog={setConfirmDialog}
                />
              ))}
            </div>

          </TabsContent>

          <TabsContent value="progress" className="space-y-4">
            <Card className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
              <CardHeader>
                <CardTitle className="text-white">Your Progress</CardTitle>
                <CardDescription>Track your athlete journey</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div 
                    className="text-center p-4 bg-blue-900 rounded-lg cursor-pointer hover:bg-blue-800 hover:ring-2 hover:ring-yellow-500 transition-all duration-200" 
                    onClick={loadUserActivities}
                  >
                    <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{user?.total_points}</p>
                    <p className="text-sm text-yellow-400">Monthly Points</p>
                    <p className="text-xs text-yellow-300 mt-1">📊 Resets monthly • Click for details</p>
                  </div>
                  <div 
                    className="text-center p-4 bg-blue-900 rounded-lg cursor-pointer hover:bg-blue-800 hover:ring-2 hover:ring-sky-400 transition-all duration-200" 
                    onClick={loadTeamComparison}
                  >
                    <Target className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{user?.level}</p>
                    <p className="text-sm text-yellow-400">Current Level</p>
                    <p className="text-xs text-sky-300 mt-1">🏆 Lifetime • Compare teammates</p>
                  </div>
                  <div className="text-center p-4 bg-blue-900 rounded-lg">
                    <Calendar className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{userProgress?.weekly_completion || 0}</p>
                    <p className="text-sm text-yellow-400">This Week</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="badges" className="space-y-4">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-white">Badges & Achievements</h2>
              <p className="text-yellow-400">Unlock badges by completing challenges and maintaining streaks</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {badges.map((badge) => (
                <Card key={badge.id} className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
                  <CardContent className="text-center p-6">
                    <div className="text-4xl mb-2">{badge.icon}</div>
                    <h3 className="font-bold text-white mb-1">{badge.name}</h3>
                    <p className="text-sm text-yellow-400 mb-3">{badge.description}</p>
                    <Badge 
                      variant={user?.badges?.includes(badge.name) ? "default" : "secondary"}
                      className={user?.badges?.includes(badge.name) ? "bg-yellow-500" : ""}
                    >
                      {user?.badges?.includes(badge.name) ? "Earned!" : "Not Earned"}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="activity" className="space-y-4">
            <ActivityTracker />
          </TabsContent>
        </Tabs>
      </div>

      {/* Activities Modal - Application Color Scheme */}
      {showActivitiesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl max-h-[80vh] bg-blue-950 border border-blue-700 rounded-lg overflow-hidden">
            <div className="bg-blue-900 border-b border-blue-700 p-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Point-Earning Activities (Last 14 Days)</h2>
                <Button 
                  onClick={() => setShowActivitiesModal(false)}
                  variant="ghost" 
                  size="sm"
                  className="text-sky-400 hover:text-white hover:bg-blue-800"
                >
                  ✕
                </Button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-96 p-4 bg-blue-950">
              {activitiesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-yellow-400">Loading activities...</div>
                </div>
              ) : activities.activities && activities.activities.length > 0 ? (
                <>
                  {/* Summary - Navy Theme */}
                  <div className="mb-6 p-4 bg-blue-900/90 border border-blue-700 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-3xl font-bold text-yellow-400">{activities.summary.total_points_earned}</p>
                        <p className="text-sm text-sky-300">Total Points Earned</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-yellow-400">{activities.summary.total_activities}</p>
                        <p className="text-sm text-sky-300">Activities Completed</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-yellow-400">{activities.summary.average_points_per_day}</p>
                        <p className="text-sm text-sky-300">Avg Points/Day</p>
                      </div>
                    </div>
                  </div>

                  {/* Activities List - Navy Theme */}
                  <div className="space-y-3">
                    {activities.activities.map((activity, index) => (
                      <div 
                        key={activity.id || index}
                        className="flex items-start justify-between p-4 bg-blue-900/70 border border-blue-700 rounded-lg border-l-4 border-l-yellow-500 hover:bg-blue-800/70"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Trophy className="w-4 h-4 text-yellow-400" />
                            <p className="font-semibold text-white">{activity.description}</p>
                            <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-medium border border-yellow-500/30">
                              +{activity.points_earned} pts
                            </span>
                          </div>
                          <p className="text-sm text-sky-300 capitalize mb-1">
                            {activity.category} • {new Date(activity.completed_at).toLocaleDateString()} at {new Date(activity.completed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </p>
                          {activity.response_text && (
                            <p className="text-xs text-sky-200 mt-2 italic bg-blue-950 p-2 rounded border border-blue-800">"{activity.response_text}"</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Trophy className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <p className="text-white text-lg">No activities found in the last 14 days.</p>
                  <p className="text-sm text-sky-300 mt-2">Complete challenges to start earning points!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Team Level Comparison Modal */}
      {showTeamComparisonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl max-h-[85vh] bg-blue-950 border border-blue-700 rounded-lg overflow-hidden">
            <div className="bg-blue-900 border-b border-blue-700 p-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Level Comparison</h2>
                <Button 
                  onClick={() => setShowTeamComparisonModal(false)}
                  variant="ghost" 
                  size="sm"
                  className="text-sky-400 hover:text-white hover:bg-blue-800"
                >
                  ✕
                </Button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[calc(85vh-80px)] p-4 bg-blue-950">
              {teamComparisonLoading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-yellow-400">Loading comparison data...</div>
                </div>
              ) : (
                <>
                  {/* Tab Selection */}
                  <div className="flex mb-6 bg-blue-900/50 rounded-lg p-1">
                    <button
                      onClick={() => setComparisonView('team')}
                      className={`flex-1 py-2 px-3 rounded-md font-semibold transition-colors text-sm ${
                        comparisonView === 'team' 
                          ? 'bg-yellow-500 text-blue-900' 
                          : 'text-white hover:bg-blue-800'
                      }`}
                      disabled={!teamComparison}
                    >
                      Team
                      {teamComparison && (
                        <span className="ml-1 text-xs">({teamComparison.total_teammates})</span>
                      )}
                    </button>
                    <button
                      onClick={() => setComparisonView('club')}
                      className={`flex-1 py-2 px-3 rounded-md font-semibold transition-colors text-sm ${
                        comparisonView === 'club' 
                          ? 'bg-yellow-500 text-blue-900' 
                          : 'text-white hover:bg-blue-800'
                      }`}
                      disabled={!teamComparison?.club_comparison_stats}
                    >
                      Club
                      {teamComparison?.club_comparison_stats && (
                        <span className="ml-1 text-xs">({teamComparison.total_club_members})</span>
                      )}
                    </button>
                    <button
                      onClick={() => setComparisonView('global')}
                      className={`flex-1 py-2 px-3 rounded-md font-semibold transition-colors text-sm ${
                        comparisonView === 'global' 
                          ? 'bg-yellow-500 text-blue-900' 
                          : 'text-white hover:bg-blue-800'
                      }`}
                    >
                      Global
                      {globalRanking && (
                        <span className="ml-1 text-xs">({globalRanking.total_users})</span>
                      )}
                    </button>
                  </div>

                  {/* Team View */}
                  {comparisonView === 'team' && teamComparison && (
                    <>
                      {/* Team Info */}
                      <div className="mb-6 p-4 bg-blue-900/90 border border-blue-700 rounded-lg">
                        <div className="text-center mb-4">
                          <h3 className="text-lg font-semibold text-white">
                            Team{teamComparison.team_names.length > 1 ? 's' : ''}: {teamComparison.team_names.join(', ')}
                          </h3>
                          <p className="text-sky-300 text-sm">
                            Your rank: #{teamComparison.comparison_stats.user_rank} of {teamComparison.comparison_stats.total_players} teammates
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-yellow-400">{teamComparison.comparison_stats.user_level}</p>
                            <p className="text-sm text-sky-300">Your Level</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-yellow-400">{teamComparison.comparison_stats.average_level}</p>
                            <p className="text-sm text-sky-300">Team Average</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-yellow-400">{teamComparison.comparison_stats.highest_level}</p>
                            <p className="text-sm text-sky-300">Highest Level</p>
                          </div>
                        </div>
                      </div>

                      {/* Teammates List */}
                      <div className="space-y-3">
                        {teamComparison.teammates.map((teammate, index) => (
                          <div 
                            key={teammate.id}
                            className={`flex items-center justify-between p-4 bg-blue-900/70 border border-blue-700 rounded-lg ${
                              teammate.is_current_user 
                                ? 'ring-2 ring-yellow-500 bg-blue-800/70' 
                                : 'hover:bg-blue-800/70'
                            }`}
                          >
                            <div className="flex items-center space-x-4">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                teammate.rank === 1 
                                  ? 'bg-yellow-500 text-black' 
                                  : teammate.rank === 2 
                                    ? 'bg-gray-400 text-black' 
                                    : teammate.rank === 3 
                                      ? 'bg-orange-600 text-white'
                                      : 'bg-blue-700 text-white'
                              }`}>
                                {teammate.rank}
                              </div>
                              <div>
                                <p className={`font-semibold ${teammate.is_current_user ? 'text-yellow-400' : 'text-white'}`}>
                                  {teammate.full_name} {teammate.is_current_user ? '(You)' : ''}
                                </p>
                                <p className="text-xs text-sky-300">
                                  {teammate.focused_sport} • {teammate.total_points} points
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-white">Lvl {teammate.level}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* No Team Message */}
                  {comparisonView === 'team' && !teamComparison && (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <p className="text-white text-lg">Not a team member yet</p>
                      <p className="text-sm text-sky-300 mt-2">Join a team to compare with teammates!</p>
                      <p className="text-xs text-gray-400 mt-4">Check out the Global rankings to see how you compare with all athletes.</p>
                    </div>
                  )}

                  {/* Club View */}
                  {comparisonView === 'club' && teamComparison?.club_comparison_stats && (
                    <>
                      {/* Club Info */}
                      <div className="mb-6 p-4 bg-blue-900/90 border border-blue-700 rounded-lg">
                        <div className="text-center mb-4">
                          <h3 className="text-lg font-semibold text-white">
                            Club: {teamComparison.club_names.join(', ')}
                          </h3>
                          <p className="text-xs text-gray-400 mb-2">
                            Teams: {teamComparison.club_team_names.join(', ')}
                          </p>
                          <p className="text-sky-300 text-sm">
                            Your rank: #{teamComparison.club_comparison_stats.user_rank} of {teamComparison.club_comparison_stats.total_players} club members
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-yellow-400">{teamComparison.club_comparison_stats.user_level}</p>
                            <p className="text-sm text-sky-300">Your Level</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-yellow-400">{teamComparison.club_comparison_stats.average_level}</p>
                            <p className="text-sm text-sky-300">Club Average</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-yellow-400">{teamComparison.club_comparison_stats.highest_level}</p>
                            <p className="text-sm text-sky-300">Highest Level</p>
                          </div>
                        </div>
                      </div>

                      {/* Club Members List */}
                      <div className="space-y-3">
                        {teamComparison.club_members.map((member, index) => (
                          <div 
                            key={member.id}
                            className={`flex items-center justify-between p-4 bg-blue-900/70 border border-blue-700 rounded-lg ${
                              member.is_current_user 
                                ? 'ring-2 ring-yellow-500 bg-blue-800/70' 
                                : 'hover:bg-blue-800/70'
                            }`}
                          >
                            <div className="flex items-center space-x-4">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                member.rank === 1 
                                  ? 'bg-yellow-500 text-black' 
                                  : member.rank === 2 
                                    ? 'bg-gray-400 text-black' 
                                    : member.rank === 3 
                                      ? 'bg-orange-600 text-white'
                                      : 'bg-blue-700 text-white'
                              }`}>
                                {member.rank}
                              </div>
                              <div>
                                <p className={`font-semibold ${member.is_current_user ? 'text-yellow-400' : 'text-white'}`}>
                                  {member.full_name} {member.is_current_user ? '(You)' : ''}
                                </p>
                                <p className="text-xs text-sky-300">
                                  {member.focused_sport} • {member.total_points} points
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-white">Lvl {member.level}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* No Club Data Message */}
                  {comparisonView === 'club' && (!teamComparison || !teamComparison.club_comparison_stats) && (
                    <div className="text-center py-8">
                      <Target className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <p className="text-white text-lg">No club data available</p>
                      <p className="text-sm text-sky-300 mt-2">Your team may not be part of a club yet.</p>
                    </div>
                  )}

                  {/* Global View */}
                  {comparisonView === 'global' && (
                    <>
                      {globalRankingLoading ? (
                        <div className="text-center py-8">
                          <div className="animate-pulse text-yellow-400">Loading global rankings...</div>
                        </div>
                      ) : globalRanking ? (
                        <>
                          {/* Global Stats */}
                          <div className="mb-6 p-4 bg-gradient-to-r from-blue-900/90 to-purple-900/90 border border-blue-700 rounded-lg">
                            <div className="text-center mb-4">
                              <h3 className="text-lg font-semibold text-white">
                                🌍 Global Grind.365 Rankings
                              </h3>
                              <p className="text-sky-300 text-sm">
                                Your rank: #{globalRanking.user_rank} of {globalRanking.total_users} athletes
                              </p>
                              {globalRanking.user_percentile && (
                                <p className="text-yellow-400 text-sm mt-1">
                                  Top {(100 - globalRanking.user_percentile).toFixed(1)}% of all athletes!
                                </p>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                              <div>
                                <p className="text-2xl font-bold text-yellow-400">{globalRanking.global_stats.user_level}</p>
                                <p className="text-sm text-sky-300">Your Level</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-yellow-400">{globalRanking.global_stats.average_level}</p>
                                <p className="text-sm text-sky-300">Global Average</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-yellow-400">{globalRanking.global_stats.highest_level}</p>
                                <p className="text-sm text-sky-300">Highest Level</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-yellow-400">{globalRanking.total_users}</p>
                                <p className="text-sm text-sky-300">Total Athletes</p>
                              </div>
                            </div>
                          </div>

                          {/* Global Leaderboard */}
                          <div className="space-y-3">
                            <h4 className="text-white font-semibold mb-3">🏆 Top Athletes</h4>
                            {globalRanking.top_users.map((athlete, index) => (
                              <div key={`${athlete.id}-${index}`}>
                                {/* Separator for "nearby" section */}
                                {athlete.is_nearby_section && index > 0 && !globalRanking.top_users[index - 1]?.is_nearby_section && (
                                  <div className="flex items-center my-4">
                                    <div className="flex-1 border-t border-blue-700"></div>
                                    <span className="px-3 text-sm text-gray-400">Your Position</span>
                                    <div className="flex-1 border-t border-blue-700"></div>
                                  </div>
                                )}
                                <div 
                                  className={`flex items-center justify-between p-4 bg-blue-900/70 border border-blue-700 rounded-lg ${
                                    athlete.is_current_user 
                                      ? 'ring-2 ring-yellow-500 bg-blue-800/70' 
                                      : 'hover:bg-blue-800/70'
                                  }`}
                                >
                                  <div className="flex items-center space-x-4">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                      athlete.rank === 1 
                                        ? 'bg-yellow-500 text-black' 
                                        : athlete.rank === 2 
                                          ? 'bg-gray-400 text-black' 
                                          : athlete.rank === 3 
                                            ? 'bg-orange-600 text-white'
                                            : 'bg-blue-700 text-white'
                                    }`}>
                                      {athlete.rank}
                                    </div>
                                    <div>
                                      <p className={`font-semibold ${athlete.is_current_user ? 'text-yellow-400' : 'text-white'}`}>
                                        {athlete.full_name} {athlete.is_current_user ? '(You)' : ''}
                                      </p>
                                      <p className="text-xs text-sky-300">
                                        {athlete.focused_sport} • {athlete.total_points} points
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-2xl font-bold text-white">Lvl {athlete.level}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <Target className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                          <p className="text-white text-lg">Unable to load global rankings</p>
                          <p className="text-sm text-sky-300 mt-2">Please try again later.</p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Points Analytics Modal */}
      {showPointsAnalyticsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl max-h-[80vh] bg-blue-950 border border-blue-700 rounded-lg overflow-hidden">
            <div className="bg-blue-900 border-b border-blue-700 p-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Points Analytics</h2>
                <Button 
                  onClick={() => setShowPointsAnalyticsModal(false)}
                  variant="ghost" 
                  size="sm"
                  className="text-sky-400 hover:text-white hover:bg-blue-800"
                >
                  ✕
                </Button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-96 p-4 bg-blue-950">
              {pointsAnalyticsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-yellow-400">Loading analytics...</div>
                </div>
              ) : pointsAnalytics ? (
                <>
                  {/* Current Points Summary */}
                  <div className="mb-6 p-4 bg-blue-900/90 border border-blue-700 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-3xl font-bold text-yellow-400">{pointsAnalytics.current_points}</p>
                        <p className="text-sm text-sky-300">Current Total Points</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-yellow-400">{pointsAnalytics.points_to_next_level}</p>
                        <p className="text-sm text-sky-300">Points to Next Level</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-yellow-400">{pointsAnalytics.current_level}</p>
                        <p className="text-sm text-sky-300">Current Level</p>
                      </div>
                    </div>
                  </div>

                  {/* Averages */}
                  <div className="mb-6 p-4 bg-blue-900/90 border border-blue-700 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-4">Average Points</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-yellow-400">{pointsAnalytics.avg_points_per_day}</p>
                        <p className="text-sm text-sky-300">Per Day</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-yellow-400">{pointsAnalytics.avg_points_per_week}</p>
                        <p className="text-sm text-sky-300">Per Week</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-yellow-400">{pointsAnalytics.avg_points_per_month}</p>
                        <p className="text-sm text-sky-300">Per Month</p>
                      </div>
                    </div>
                  </div>

                  {/* Module Breakdown */}
                  <div className="p-4 bg-blue-900/90 border border-blue-700 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-4">Module Performance</h3>
                    <div className="space-y-3">
                      {pointsAnalytics.module_breakdown.map((module) => (
                        <div 
                          key={module.category}
                          className="p-3 bg-blue-900/70 border border-blue-700 rounded-lg"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium capitalize">{module.category}</span>
                            <span className="text-yellow-400 font-bold">{module.total_points} pts</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-sky-300">{module.completion_count} completions</span>
                            {module.is_missing && (
                              <span className="text-red-400 text-xs">⚠️ Missing points here</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <p className="text-white text-lg">Unable to load analytics</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Badges Modal */}
      {showBadgesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl max-h-[80vh] bg-blue-950 border border-blue-700 rounded-lg overflow-hidden">
            <div className="bg-blue-900 border-b border-blue-700 p-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Your Earned Badges</h2>
                <Button 
                  onClick={() => setShowBadgesModal(false)}
                  variant="ghost" 
                  size="sm"
                  className="text-sky-400 hover:text-white hover:bg-blue-800"
                >
                  ✕
                </Button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-96 p-4 bg-blue-950">
              {user?.badges && user.badges.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {badges.filter(badge => user.badges.includes(badge.name)).map((badge) => (
                    <Card key={badge.id} className="border-0 shadow-lg bg-blue-900/95 backdrop-blur-sm border border-blue-700">
                      <CardContent className="text-center p-6">
                        <div className="text-4xl mb-2">{badge.icon}</div>
                        <h3 className="font-bold text-white mb-1">{badge.name}</h3>
                        <p className="text-sm text-yellow-400 mb-3">{badge.description}</p>
                        <Badge className="bg-yellow-500">Earned!</Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <p className="text-white text-lg">No badges earned yet</p>
                  <p className="text-sm text-sky-300 mt-2">Complete challenges and maintain streaks to earn badges!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sport Focus Edit Modal */}
      {showSportFocusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-2xl max-h-[90vh] bg-blue-950 border border-blue-700 rounded-lg overflow-hidden">
            <div className="bg-blue-900 border-b border-blue-700 p-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Edit Sport Focus</h2>
                <Button 
                  onClick={() => setShowSportFocusModal(false)}
                  variant="ghost" 
                  size="sm"
                  className="text-sky-400 hover:text-white hover:bg-blue-800"
                >
                  ✕
                </Button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-4 bg-blue-950">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="sport" className="text-white">Sport</Label>
                  <Input
                    id="sport"
                    type="text"
                    value={sportFocusValue}
                    onChange={(e) => setSportFocusValue(e.target.value)}
                    placeholder="e.g., Soccer, Basketball, Football"
                    className="bg-blue-900/50 text-white placeholder:text-gray-400 border-blue-700"
                  />
                </div>

                {/* Key Strengths */}
                <div className="space-y-2">
                  <Label className="text-white">Your Key Strengths (Select at least 3)</Label>
                  <div className="grid grid-cols-2 gap-2 text-sm max-h-48 overflow-y-auto bg-blue-900/30 p-3 rounded border border-blue-700">
                    {['Speed', 'Endurance', 'Agility', 'Strength', 'Coordination', 'Balance', 'Flexibility', 'Power', 'Accuracy', 'Technique', 'Strategy', 'Leadership', 'Communication', 'Focus', 'Composure'].map((strength) => (
                      <label key={strength} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={sportStrengths.includes(strength.toLowerCase())}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSportStrengths([...sportStrengths, strength.toLowerCase()]);
                            } else {
                              setSportStrengths(sportStrengths.filter(s => s !== strength.toLowerCase()));
                            }
                          }}
                          className="rounded border-blue-300"
                        />
                        <span className="text-white">{strength}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-sm text-yellow-400">Selected: {sportStrengths.length}</p>
                </div>

                {/* Areas to Improve */}
                <div className="space-y-2">
                  <Label className="text-white">Areas to Improve (Select at least 3)</Label>
                  <div className="grid grid-cols-2 gap-2 text-sm max-h-48 overflow-y-auto bg-blue-900/30 p-3 rounded border border-blue-700">
                    {['First Touch', 'Weak Foot/Hand', 'Positioning', 'Decision Making', 'Physical Strength', 'Stamina', 'Concentration', 'Confidence', 'Communication', 'Mental Toughness', 'Game Reading', 'Reaction Time', 'Consistency', 'Pressure Handling', 'Recovery'].map((area) => (
                      <label key={area} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={sportWeaknesses.includes(area.toLowerCase())}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSportWeaknesses([...sportWeaknesses, area.toLowerCase()]);
                            } else {
                              setSportWeaknesses(sportWeaknesses.filter(a => a !== area.toLowerCase()));
                            }
                          }}
                          className="rounded border-blue-300"
                        />
                        <span className="text-white">{area}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-sm text-yellow-400">Selected: {sportWeaknesses.length}</p>
                </div>

                <p className="text-sm text-sky-300">
                  Changing your sport focus, strengths, and areas to improve will update your daily affirmations and visualizations to match your athletic profile.
                </p>
                <Button
                  onClick={handleSportFocusUpdate}
                  className="w-full bg-gradient-to-r from-blue-900 to-yellow-500 hover:from-blue-800 hover:to-yellow-600"
                  disabled={sportStrengths.length < 3 || sportWeaknesses.length < 3}
                >
                  Update Sport Focus
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Switch to Video Review - Bottom of Dashboard */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        <div className="border-t border-slate-700 pt-6">
          <button
            onClick={() => {
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              setGrindDate(yesterday.toISOString().split('T')[0]);
              setShowVideoReview(true);
            }}
            className="w-full p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-purple-500/50 hover:bg-slate-800 transition-all group"
            data-testid="switch-to-video-review-btn"
          >
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Switch to Video Review
                </p>
                <p className="text-slate-400 text-sm">
                  Log game stats from yesterday's session
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>
      
      </> 
      )}
      
      {/* Global Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
      />
    </div>
  );
};

// Main Dashboard Router Component

export default Dashboard;
